import type { AppSidebarData } from '@repo/ui';
import {
  AudioLines,
  BarChart3,
  Bell,
  Briefcase,
  Clapperboard,
  FolderTree,
  LayoutDashboard,
  Settings,
} from 'lucide-react';

type NavGroup = AppSidebarData['navMain'][number];
type NavGroupSpec = Omit<NavGroup, 'isActive'> & { activeUrls?: string[] };

const ADMIN_NAV_GROUPS: NavGroupSpec[] = [
  { title: 'Dashboard', url: '/sys', icon: LayoutDashboard },
  {
    title: 'Content',
    url: '/sys/collections',
    icon: FolderTree,
    activeUrls: [
      '/sys/collections',
      '/sys/challenges',
      '/sys/media',
      '/sys/subtitles',
      '/sys/pipeline',
      '/sys/triage',
      '/sys/translations',
    ],
    items: [
      { title: 'Collections', url: '/sys/collections' },
      { title: 'Challenges', url: '/sys/challenges' },
      { title: 'Triage', url: '/sys/triage' },
      { title: 'Translations', url: '/sys/translations' },
      { title: 'Media', url: '/sys/media' },
      { title: 'Subtitles', url: '/sys/subtitles' },
      { title: 'Pipeline', url: '/sys/pipeline' },
    ],
  },
  {
    title: 'Audio',
    url: '/sys/audio',
    icon: AudioLines,
    activeUrls: ['/sys/audio'],
    items: [
      { title: 'Dashboard', url: '/sys/audio' },
      { title: 'Generate', url: '/sys/audio/generate' },
    ],
  },
  {
    title: 'Social Media',
    url: '/sys/social',
    icon: Clapperboard,
    activeUrls: ['/sys/social'],
    items: [
      { title: 'Dashboard', url: '/sys/social' },
      { title: 'Videos', url: '/sys/social/videos' },
      { title: 'Channels', url: '/sys/social/channels' },
      { title: 'History', url: '/sys/social/history' },
    ],
  },
  {
    title: 'Monitoring',
    url: '/sys/users',
    icon: BarChart3,
    activeUrls: [
      '/sys/users',
      '/sys/reviewers',
      '/sys/challenge-issues',
      '/sys/monitoring',
      '/sys/leaderboard',
    ],
    items: [
      { title: 'Users', url: '/sys/users' },
      { title: 'Reviewers', url: '/sys/reviewers' },
      { title: 'Challenge Issues', url: '/sys/challenge-issues' },
      { title: 'Learning', url: '/sys/monitoring/learning' },
      { title: 'Content Health', url: '/sys/monitoring/content' },
      { title: 'Leaderboard', url: '/sys/leaderboard' },
    ],
  },
  {
    title: 'Push',
    url: '/sys/notifications',
    icon: Bell,
    activeUrls: ['/sys/notifications'],
  },
  {
    title: 'Business',
    url: '/sys/billing',
    icon: Briefcase,
    activeUrls: ['/sys/billing', '/sys/ticket', '/sys/telegram'],
    items: [
      { title: 'Billing', url: '/sys/billing' },
      { title: 'Tickets', url: '/sys/ticket' },
      { title: 'Telegram', url: '/sys/telegram' },
    ],
  },
  {
    title: 'System',
    url: '/sys/database',
    icon: Settings,
    activeUrls: [
      '/sys/database',
      '/sys/feature-flags',
      '/sys/upload-queue',
      '/sys/configs',
      '/sys/debug',
    ],
    items: [
      { title: 'Database', url: '/sys/database' },
      { title: 'Feature Flags', url: '/sys/feature-flags' },
      { title: 'Upload Queue', url: '/sys/upload-queue' },
      { title: 'Configs', url: '/sys/configs' },
      { title: 'Debug', url: '/sys/debug' },
    ],
  },
];

export function getAdminSysNavItems(
  isInGroup: (urls: string[]) => boolean,
): AppSidebarData['navMain'] {
  return ADMIN_NAV_GROUPS.map(({ activeUrls, ...group }) =>
    activeUrls ? { ...group, isActive: isInGroup(activeUrls) } : group,
  );
}
